import React from 'react';
import { Dialog, DialogTitle, DialogContent, Grid, Paper, Typography, Box, Chip, Divider, IconButton, LinearProgress, Stack, Button, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const scoreToColorModal = (score) => {
  const numericScore = Number(score);
  if (isNaN(numericScore)) return '#808080';
  return numericScore <= 50 ? "#D0312D" : numericScore <= 75 ? "#FC6A03" : "#028A0F";
};

const ScoreBreakdown = ({ title, criteria, results }) => {
    if (!Array.isArray(criteria)) {
        return <Typography variant="body2" color="text.secondary">Scorecard criteria not available.</Typography>;
    }

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1.5, fontWeight: 600 }}>{title}</Typography>
            <Stack spacing={2}>
                {criteria.map(criterion => {
                    const result = results.find(r => r.id === criterion.id);
                    // Ensure criterion.levels is an array before trying to find an index
                    const levelIndex = result && Array.isArray(criterion.levels) ? criterion.levels.findIndex(l => l.id === result.value) : -1;
                    const score = levelIndex !== -1 && criterion.levels.length > 1 ? (levelIndex / (criterion.levels.length - 1)) : 0;
                    const percentage = Math.round(score * 100);

                    return (
                        <Box key={criterion.id}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="body2">{criterion.name}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: scoreToColorModal(percentage) }}>{percentage}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={percentage} sx={{ height: 6, borderRadius: 3 }} color={percentage > 75 ? 'success' : (percentage > 50 ? 'warning' : 'error')} />
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};

const ComparisonColumn = ({ candidate, idealCandidate }) => {
    const skillScore = Math.round((candidate.scorecardResults.find(s => s.scorecardName === "skills")?.score || 0) * 100);
    const behaviorScore = Math.round((candidate.scorecardResults.find(s => s.scorecardName === "behaviors")?.score || 0) * 100);
    const skillResults = candidate.scorecardResults.find(s => s.scorecardName === "skills")?.criteria || [];
    const behaviorResults = candidate.scorecardResults.find(s => s.scorecardName === "behaviors")?.criteria || [];

    return (
        <Paper variant="outlined" sx={{ p: 2.5, height: '100%', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>{candidate.candidate.name}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Overall Scores</Typography>
            <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                <Chip label={`Skill: ${skillScore}%`} sx={{ bgcolor: scoreToColorModal(skillScore), color: '#fff' }} />
                <Chip label={`Behavior: ${behaviorScore}%`} sx={{ bgcolor: scoreToColorModal(behaviorScore), color: '#fff' }} />
            </Box>
            
            {idealCandidate && (
                <>
                    <ScoreBreakdown title="Skills Breakdown" criteria={idealCandidate.skills} results={skillResults} />
                    <ScoreBreakdown title="Behaviors Breakdown" criteria={idealCandidate.behaviors} results={behaviorResults} />
                </>
            )}
        </Paper>
    );
};

export default function CandidateComparisonModal({ open, onClose, candidates = [], idealCandidate }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Candidate Comparison
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ py: 2 }}>
          {candidates.map(candidate => (
            <Grid item xs={12} md={candidates.length > 0 ? 12 / candidates.length : 12} key={candidate.id}>
              <ComparisonColumn candidate={candidate} idealCandidate={idealCandidate} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
